import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { t, L } from "../lib/translations";
import { useNavigate, Link } from "react-router-dom";
import {
  Zap, Shield, Star, Headphones, ArrowRight, Users, TrendingUp, Sparkles,
  Clock3, Gift, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, ChevronUp,
  X, ExternalLink, Info, AlertTriangle,
  AlertCircle, CheckCircle, Lock, BadgeCheck, Rocket, Globe2, BarChart3,
  Heart, Eye, Play, Activity
} from "lucide-react";

// ── Platform SVG Icons ────────────────────────────────────────────────────────
import {
  InstagramSVG, TikTokSVG, YouTubeSVG, TwitchSVG, TelegramSVG,
  TwitterSVG, FacebookSVG, DiscordSVG, LinkedInSVG, SnapchatSVG,
  SpotifySVG, VKSVG, KickSVG,
} from "../components/PlatformIcons";


// ── System Banners ────────────────────────────────────────────────────────────
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BANNER_STYLES = {
  info: { bg: "rgba(64,93,230,0.12)", border: "rgba(64,93,230,0.3)", color: "#7aa2ff", Icon: Info },
  warning: { bg: "rgba(247,119,55,0.12)", border: "rgba(247,119,55,0.35)", color: "#F77737", Icon: AlertTriangle },
  error: { bg: "rgba(255,107,53,0.12)", border: "rgba(255,107,53,0.35)", color: "#FF6B35", Icon: AlertCircle },
  success: { bg: "rgba(34,197,94,0.12)", border: "rgba(34,197,94,0.3)", color: "#22c55e", Icon: CheckCircle },
  promo: { bg: "rgba(131,58,180,0.12)", border: "rgba(131,58,180,0.35)", color: "#c084fc", Icon: Sparkles },
};

function SystemBanners({ lang }) {
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dismissed_banners") || "[]"); } catch { return []; }
  });
  useEffect(() => {
    fetch(`${API}/site/banners`).then(r => r.ok ? r.json() : []).then(d => setBanners(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);
  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    localStorage.setItem("dismissed_banners", JSON.stringify(next));
  };
  const visible = banners.filter(b => !dismissed.includes(b.banner_id));
  if (!visible.length) return null;
  const L = (obj) => (obj || {})[lang] || (obj || {}).en || (obj || {}).ru || (obj || {}).he || "";
  return (
    <div className="w-full space-y-1.5 px-4 pt-2 pb-1 max-w-5xl mx-auto">
      {visible.map(b => {
        const s = BANNER_STYLES[b.type] || BANNER_STYLES.info;
        const { Icon } = s;
        const msg = L(b.message);
        const linkLabel = b.link_url ? L(b.link_label) || "→" : null;
        return (
          <div key={b.banner_id} className="flex items-start gap-3 px-4 py-3 rounded-2xl text-sm animate-fade-in"
            style={{ background: s.bg, border: `1px solid ${s.border}` }}>
            <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: s.color }} />
            <span className="flex-1 leading-snug" style={{ color: "var(--text-primary)" }}>
              {msg}
              {b.link_url && (
                <a href={b.link_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-semibold ms-2 hover:underline"
                  style={{ color: s.color }}>
                  {linkLabel} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </span>
            {b.dismissible !== false && (
              <button onClick={() => dismiss(b.banner_id)} aria-label="Dismiss banner" className="flex-shrink-0 hover:opacity-70 transition-opacity">
                <X className="w-3.5 h-3.5" style={{ color: s.color }} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Live Order Ticker ──────────────────────────────────────────────────────────
function LiveOrderTicker({ lang }) {
  const ORDERS = [
    { he: 'דניאל מת"א הזמין 1,000 עוקבים', ru: "Даниэль из Тель-Авива заказал 1,000 подписчиков", en: "Daniel from Tel Aviv ordered 1,000 followers", ago: "2" },
    { he: "שירה מחיפה הזמינה 500 לייקים", ru: "Шира из Хайфы заказала 500 лайков", en: "Shira from Haifa ordered 500 likes", ago: "5" },
    { he: "אורן מבאר שבע הזמין 2,000 עוקבים", ru: "Орен из Беэр-Шевы заказал 2,000 подписчиков", en: "Oren from Beer Sheva ordered 2,000 followers", ago: "8" },
    { he: "מיה מירושלים הזמינה 300 צפיות", ru: "Майя из Иерусалима заказала 300 просмотров", en: "Maya from Jerusalem ordered 300 views", ago: "11" },
    { he: "יוסי מרמת גן הזמין 800 לייקים", ru: "Йоси из Рамат-Гана заказал 800 лайков", en: "Yossi from Ramat Gan ordered 800 likes", ago: "14" },
    { he: "לירן מנתניה הזמין 3,000 עוקבים", ru: "Лиран из Нетании заказал 3,000 подписчиков", en: "Liran from Netanya ordered 3,000 followers", ago: "18" },
  ];
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = setInterval(() => {
      setVisible(false);
      setTimeout(() => { setIdx(i => (i + 1) % ORDERS.length); setVisible(true); }, 350);
    }, 4000);
    return () => clearInterval(timer);
  }, []);
  const o = ORDERS[idx];
  const text = lang === "he" ? o.he : lang === "ru" ? o.ru : o.en;
  return (
    <div className="inline-flex items-center gap-2.5 rounded-full px-4 py-2 text-[12px] font-medium" dir="ltr"
      style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)", backdropFilter: "blur(10px)" }}>
      <span className="w-2 h-2 rounded-full flex-shrink-0 animate-pulse" style={{ background: "#10B981", boxShadow: "0 0 8px #10B981" }} />
      <span key={idx} className={visible ? "animate-ticker" : ""} style={{ color: "rgba(255,255,255,0.8)", direction: lang === "he" ? "rtl" : "ltr", unicodeBidi: "embed" }}>{text}</span>
      <span style={{ color: "rgba(255,255,255,0.35)" }}>• {o.ago}m ago</span>
    </div>
  );
}

// ── Animated Counter ───────────────────────────────────────────────────────────
function AnimatedCounter({ to, suffix = "", prefix = "" }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVal(to);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      obs.disconnect();
      let start = 0;
      const duration = 1800;
      const step = 16;
      const inc = to / (duration / step);
      const timer = setInterval(() => {
        start += inc;
        if (start >= to) { setVal(to); clearInterval(timer); }
        else setVal(Math.floor(start));
      }, step);
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [to]);
  const display = val >= 1000 ? (val / 1000).toFixed(val % 1000 === 0 ? 0 : 1) + "K" : val.toString();
  return <span ref={ref}>{prefix}{display}{suffix}</span>;
}

// ── Single Review Card ────────────────────────────────────────────────────────
function ReviewCard({ review, lang }) {
  return (
    <div className="review-card-hover rounded-2xl p-5 flex flex-col h-full"
      style={{ background: "var(--surface-1)", border: `1px solid ${review.color}30` }}>
      <div className="result-badge-anim inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold mb-3 self-start"
        style={{ background: `${review.color}14`, border: `1px solid ${review.color}35`, color: review.color }}>
        <review.icon className="w-2.5 h-2.5 flex-shrink-0" />
        {review.result}
      </div>
      <div className="flex items-center gap-0.5 mb-3">
        {[1,2,3,4,5].map(j => <Star key={j} className="w-3 h-3" style={{ fill:"#FBBF24", color:"#FBBF24" }} />)}
        <span className="text-[9px] ms-1.5 font-semibold px-1.5 py-0.5 rounded-full"
          style={{ color:"var(--text-secondary)", background:"var(--surface-2)" }}>
          {t("Landing.verified", lang)}
        </span>
      </div>
      <p className="text-xs leading-5 mb-4 flex-1" style={{ color:"var(--text-secondary)" }}>"{review.text}"</p>
      <div className="flex items-center gap-2.5 mt-auto">
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black text-white flex-shrink-0"
          style={{ background: `linear-gradient(135deg,${review.avatar},${review.color})` }}>
          {review.name[0]}
        </div>
        <div className="min-w-0">
          <div className="text-xs font-bold truncate" style={{ color:"var(--text-primary)" }}>{review.name}</div>
          <div className="text-[10px] truncate" style={{ color:"var(--text-secondary)" }}>{review.service}</div>
        </div>
        <BadgeCheck className="w-3.5 h-3.5 ms-auto flex-shrink-0" style={{ color:"#10B981" }} />
      </div>
    </div>
  );
}

// ── Rotating Review Carousel (max 5 visible, auto-rotates) ────────────────────
function ReviewCarousel({ reviews, lang }) {
  const PER_PAGE = 5;
  const numPages = Math.ceil(reviews.length / PER_PAGE);
  const [page, setPage] = useState(0);
  const [show, setShow] = useState(true);
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const goTo = useCallback((next) => {
    if (reducedMotion) { setPage(next); return; }
    setShow(false);
    setTimeout(() => { setPage(next); setShow(true); }, 280);
  }, [reducedMotion]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const iv = setInterval(() => goTo((page + 1) % numPages), 7000);
    return () => clearInterval(iv);
  }, [page, numPages, goTo]);

  const cards = Array.from({ length: PER_PAGE }, (_, i) =>
    reviews[(page * PER_PAGE + i) % reviews.length]
  );

  const btnStyle = {
    background: "var(--surface-1)",
    border: "1px solid var(--surface-2)",
    color: "var(--text-secondary)",
    minWidth: 44,
    minHeight: 44,
    touchAction: "manipulation",
  };

  return (
    <div>
      <div style={{ opacity: show ? 1 : 0, transition: reducedMotion ? "none" : "opacity 280ms ease" }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {cards.map((review, i) => (
            <ReviewCard key={`${page}-${i}`} review={review} lang={lang} />
          ))}
        </div>
      </div>

      {/* Navigation — pill with arrows + dots, RTL-aware */}
      <div className="flex items-center justify-center mt-8" dir={lang === "he" ? "rtl" : "ltr"}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 0,
          background: "var(--surface-1)", border: "1px solid var(--surface-2)",
          borderRadius: 999, padding: "5px 6px",
        }}>
          {/* Prev */}
          <button
            onClick={() => goTo((page - 1 + numPages) % numPages)}
            aria-label="Previous"
            style={{
              width: 36, height: 36, borderRadius: "50%", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "transparent", color: "var(--text-secondary)",
              transition: "background 0.2s", flexShrink: 0,
            }}
            onMouseEnter={e => e.currentTarget.style.background = "var(--surface-2)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            {lang === "he" ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>

          {/* Dots */}
          <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "0 8px" }}>
            {Array.from({ length: numPages }, (_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Page ${i + 1}`}
                style={{
                  height: 7, borderRadius: 999, border: "none", cursor: "pointer",
                  width: i === page ? 20 : 7,
                  background: i === page ? "#E1306C" : "var(--surface-2)",
                  transition: "all 0.3s ease", padding: 0, flexShrink: 0,
                  opacity: i === page ? 1 : 0.5,
                }}
              />
            ))}
          </div>

          {/* Next */}
          <button
            onClick={() => goTo((page + 1) % numPages)}
            aria-label="Next"
            style={{
              width: 36, height: 36, borderRadius: "50%", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "transparent", color: "var(--text-secondary)",
              transition: "background 0.2s", flexShrink: 0,
            }}
            onMouseEnter={e => e.currentTarget.style.background = "var(--surface-2)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            {lang === "he" ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const { user, login } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [faqOpen, setFaqOpen] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const h = (e) => setSupportOpen(e.detail?.open ?? false);
    window.addEventListener("support-open-state", h);
    return () => window.removeEventListener("support-open-state", h);
  }, []);

  const shuffledReviews = useMemo(() => {
    const pool = [
      { name: L(lang,"Noa H.","Ноа Х.","נועה ה."), avatar:"#833AB4", result: L(lang,"+520 followers · 4h","+520 подписчиков · 4ч","+520 עוקבים · 4 שעות"), service:"Instagram Followers", color:"#E1306C", icon:Users, text: L(lang,"Got 520 real followers within 4 hours. My profile finally looks credible and I'm getting new followers organically too.","Получила 520 реальных подписчиков за 4 часа. Профиль наконец выглядит серьёзно, и начали приходить органические подписчики.","קיבלתי 520 עוקבים אמיתיים תוך 4 שעות. הפרופיל שלי סוף סוף נראה אמין ומתחילים להגיע עוד עוקבים אורגניים.") },
      { name: L(lang,"Omer L.","Омер Л.","עומר ל."), avatar:"#E1306C", result: L(lang,"+280 likes · 2h","+280 лайков · 2ч","+280 לייקים · 2 שעות"), service:"Instagram Likes", color:"#E1306C", icon:Heart, text: L(lang,"Ordered likes for a product launch post. Engagement shot up and the post got way more organic reach than usual.","Заказал лайки для поста о запуске продукта. Охват резко вырос, пост получил намного больше органики.","הזמנתי לייקים לפוסט השקה. המעורבות קפצה והפוסט קיבל הרבה יותר חשיפה אורגנית מהרגיל.") },
      { name: L(lang,"Maya R.","Майя Р.","מיה ר."), avatar:"#EE1D52", result: L(lang,"+650 TikTok followers · 6h","+650 подписчиков TikTok · 6ч","+650 עוקבים TikTok · 6 שעות"), service:"TikTok Followers", color:"#EE1D52", icon:Users, text: L(lang,"Started getting recommended by the algorithm after my follower count jumped. The growth paid for itself within days.","После роста числа подписчиков алгоритм начал рекомендовать меня. Вложения окупились за несколько дней.","האלגוריתם התחיל להמליץ עליי אחרי שמספר העוקבים קפץ. ההשקעה החזירה את עצמה תוך ימים.") },
      { name: L(lang,"Shachar B.","Шахар Б.","שחר ב."), avatar:"#69C9D0", result: L(lang,"+1,800 TikTok views · 1h","+1,800 просмотров TikTok · 1ч","+1,800 צפיות TikTok · שעה"), service:"TikTok Views", color:"#EE1D52", icon:Eye, text: L(lang,"My video hit the FYP within an hour of ordering views. Crazy how fast it worked.","Моё видео попало в рекомендации FYP через час после заказа просмотров. Невероятно быстро.","הוידאו שלי נכנס לעמוד לך בשבילך תוך שעה מהזמנת הצפיות. לא האמנתי כמה מהר זה עבד.") },
      { name: L(lang,"Aviv S.","Авив С.","אביב ס."), avatar:"#2AABEE", result: L(lang,"+380 Telegram members · 5h","+380 участников Telegram · 5ч","+380 חברים Telegram · 5 שעות"), service:"Telegram Members", color:"#2AABEE", icon:Users, text: L(lang,"Group went from dead to active. New real members started joining organically after the count grew.","Группа ожила. После роста числа участников начали вступать реальные люди органически.","הקבוצה עברה ממתה לפעילה. אחרי שמספר החברים גדל, התחילו להצטרף חברים אמיתיים אורגנית.") },
      { name: L(lang,"Ran T.","Ран Т.","רן ת."), avatar:"#1E90FF", result: L(lang,"+2,200 Telegram views · 30min","+2,200 просмотров Telegram · 30мин","+2,200 צפיות Telegram · 30 דקות"), service:"Telegram Views", color:"#2AABEE", icon:Eye, text: L(lang,"Post views went up immediately. The channel started looking active and our subscribers grew naturally after.","Просмотры выросли мгновенно. Канал начал выглядеть активным, подписчики стали расти сами.","הצפיות על הפוסט עלו מיד. הערוץ התחיל להיראות פעיל והמנויים גדלו לבד אחרי כן.") },
      { name: L(lang,"Guy M.","Гай М.","גיא מ."), avatar:"#9146FF", result: L(lang,"+170 Twitch followers · 3h","+170 подписчиков Twitch · 3ч","+170 עוקבים Twitch · 3 שעות"), service:"Twitch Followers", color:"#9146FF", icon:Users, text: L(lang,"Hit Affiliate threshold much faster with the follower boost. Totally changed the trajectory of my channel.","Достиг порога Affiliate намного быстрее благодаря буст подписчиков. Это изменило траекторию моего канала.","הגעתי לסף של Affiliate הרבה יותר מהר עם בוסט העוקבים. זה שינה לחלוטין את המסלול של הערוץ שלי.") },
      { name: L(lang,"Dana K.","Дана К.","דנה כ."), avatar:"#6441A4", result: L(lang,"+75 live viewers · peak stream","+75 зрителей · пик стрима","+75 צופים חיים · שיא שידור"), service:"Twitch Live Viewers", color:"#9146FF", icon:Activity, text: L(lang,"Stream was featured on the browse page for the first time after my viewer count jumped. Great investment.","Стрим впервые попал на страницу обзора после роста зрителей. Отличная инвестиция.","השידור הופיע בדף הגלישה בפעם הראשונה אחרי שמספר הצופים קפץ. השקעה מצוינת.") },
      { name: L(lang,"Tamar G.","Тамар Г.","תמר ג."), avatar:"#1877F2", result: L(lang,"+450 Facebook followers · 4h","+450 подписчиков Facebook · 4ч","+450 עוקבים Facebook · 4 שעות"), service:"Facebook Followers", color:"#1877F2", icon:Users, text: L(lang,"Business page looked empty before. After the boost, new clients mentioned finding us on Facebook. Worth every shekel.","Страница бизнеса выглядела пустой. После буста новые клиенты стали упоминать, что нашли нас в Facebook. Каждый шекель окупился.","הדף העסקי נראה ריק לפני כן. אחרי הבוסט לקוחות חדשים ציינו שמצאו אותנו בפייסבוק. שווה כל שקל.") },
      { name: L(lang,"Or P.","Ор П.","אור פ."), avatar:"#0866FF", result: L(lang,"+310 page likes · 3h","+310 лайков страницы · 3ч","+310 לייקים לדף · 3 שעות"), service:"Facebook Page Likes", color:"#1877F2", icon:Heart, text: L(lang,"Page likes doubled which pushed our posts into more feeds. The organic reach increased noticeably afterward.","Лайки страницы удвоились, посты стали показываться большему числу людей. Органический охват заметно вырос.","לייקים לדף הוכפלו וזה דחף את הפוסטים שלנו ליותר פידים. ההגעה האורגנית עלתה ניכרת אחר כך.") },
      { name: L(lang,"Yoav N.","Йоав Н.","יואב נ."), avatar:"#1A8CD8", result: L(lang,"+390 X followers · 5h","+390 подписчиков X · 5ч","+390 עוקבים X · 5 שעות"), service:"X / Twitter Followers", color:"#1A8CD8", icon:Users, text: L(lang,"Account started appearing in search suggestions after the follower count grew. My threads get way more impressions now.","Аккаунт появился в подсказках поиска после роста подписчиков. Мои треды получают намного больше показов.","החשבון התחיל להופיע בהצעות חיפוש אחרי שמספר העוקבים גדל. הת'רדים שלי מקבלים הרבה יותר חשיפות עכשיו.") },
      { name: L(lang,"Shira E.","Шира Э.","שירה א."), avatar:"#333", result: L(lang,"+240 X likes · 1h","+240 лайков X · 1ч","+240 לייקים X · שעה"), service:"X / Twitter Likes", color:"#1A8CD8", icon:Heart, text: L(lang,"Tweet went viral after the likes kick-started the algorithm. Saved me hours of manual promotion.","Твит разошёлся после того, как лайки запустили алгоритм. Сэкономил часы ручного продвижения.","הציוץ הפך לויראלי אחרי שהלייקים הפעילו את האלגוריתם. חסך לי שעות של קידום ידני.") },
      { name: L(lang,"Natan A.","Натан А.","נתן א."), avatar:"#0A66C2", result: L(lang,"+230 LinkedIn followers · 6h","+230 подписчиков LinkedIn · 6ч","+230 עוקבים LinkedIn · 6 שעות"), service:"LinkedIn Followers", color:"#0A66C2", icon:Users, text: L(lang,"Profile views tripled and I started receiving connection requests from relevant professionals. Huge for B2B.","Просмотры профиля утроились, начали приходить запросы на связь от нужных профессионалов. Огромно для B2B.","הצפיות בפרופיל הוכפלו פי שלוש ואני מקבל בקשות חיבור ממקצועני תעשייה רלוונטיים. ענק לB2B.") },
      { name: L(lang,"Rotem H.","Ротем Х.","רותם ה."), avatar:"#004182", result: L(lang,"+85 post likes · 2h","+85 лайков поста · 2ч","+85 לייקים לפוסט · 2 שעות"), service:"LinkedIn Likes", color:"#0A66C2", icon:Heart, text: L(lang,"Post got pinned in my network's feed for days thanks to the engagement boost. Several new clients reached out.","Пост держался в топе ленты несколько дней благодаря буст. Несколько новых клиентов написали мне.","הפוסט נשאר בראש הפיד של הרשת שלי ימים הודות לבוסט המעורבות. כמה לקוחות חדשים פנו אליי.") },
      { name: L(lang,"Eitan M.","Эйтан М.","עידן מ."), avatar:"#53FC18", result: L(lang,"+140 Kick followers · 4h","+140 подписчиков Kick · 4ч","+140 עוקבים Kick · 4 שעות"), service:"Kick Followers", color:"#53FC18", icon:Users, text: L(lang,"New platform, needed a quick start. The follower boost made my channel look active and discovery improved a lot.","Новая платформа, нужен был быстрый старт. Буст подписчиков сделал канал активным, и обнаруживаемость резко выросла.","פלטפורמה חדשה, הייתי צריך התחלה מהירה. בוסט העוקבים גרם לערוץ שלי להיראות פעיל ויכולת הגילוי עלתה מאוד.") },
      { name: L(lang,"Liron D.","Лирон Д.","לירון ד."), avatar:"#3db810", result: L(lang,"+60 live viewers · stream peak","+60 зрителей · пик","+60 צופים חיים · שיא"), service:"Kick Live Viewers", color:"#53FC18", icon:Activity, text: L(lang,"Kick suggested my stream to other users once the viewer count hit a threshold. Best investment for a new streamer.","Kick начал рекомендовать мой стрим другим пользователям, как только число зрителей достигло порога. Лучшая инвестиция для нового стримера.","Kick התחיל להמליץ על השידור שלי למשתמשים אחרים ברגע שמספר הצופים הגיע לסף. ההשקעה הטובה ביותר לסטרימר חדש.") },
    ];
    return [...pool].sort(() => 0.5 - Math.random());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const PLATFORMS = [
    { Icon: InstagramSVG, name: "Instagram",  platformKey: "instagram",  services: t("Landing.followers_likes_views_reels", lang), color: "#E1306C", gradient: "linear-gradient(135deg,#833AB4,#E1306C)", available: true },
    { Icon: TikTokSVG,    name: "TikTok",     platformKey: "tiktok",     services: t("Landing.followers_views_likes", lang),       color: "#EE1D52", gradient: "linear-gradient(135deg,#69C9D0,#EE1D52)", available: true },
    { Icon: TelegramSVG,  name: "Telegram",   platformKey: "telegram",   services: t("Landing.members_views_reactions", lang),    color: "#2AABEE", gradient: "linear-gradient(135deg,#1E90FF,#2AABEE)", available: true },
    { Icon: TwitchSVG,    name: "Twitch",     platformKey: "twitch",     services: t("Landing.followers_live_viewers", lang),           color: "#9146FF", gradient: "linear-gradient(135deg,#6441A4,#9146FF)", available: true },
    { Icon: FacebookSVG,  name: "Facebook",   platformKey: "facebook",   services: t("Landing.followers_page_likes_shares", lang), color: "#1877F2", gradient: "linear-gradient(135deg,#0866FF,#1877F2)", available: true },
    { Icon: TwitterSVG,   name: "X / Twitter",platformKey: "twitter",    services: t("Landing.followers_likes_reposts", lang),    color: "#1A8CD8", gradient: "linear-gradient(135deg,#333,#1A8CD8)", available: true },
    { Icon: LinkedInSVG,  name: "LinkedIn",   platformKey: "linkedin",   services: t("Landing.followers_likes", lang),                                   color: "#0A66C2", gradient: "linear-gradient(135deg,#0A66C2,#004182)", available: true },
    { Icon: KickSVG,      name: "Kick",       platformKey: "kick",       services: t("Landing.followers_live_viewers", lang),           color: "#53FC18", gradient: "linear-gradient(135deg,#3db810,#53FC18)", available: true },
    { Icon: YouTubeSVG,   name: "YouTube",    platformKey: "youtube",    services: t("Landing.subscribers_views_likes", lang),    color: "#FF0000", gradient: "linear-gradient(135deg,#FF0000,#CC0000)", available: false },
    { Icon: DiscordSVG,   name: "Discord",    platformKey: "discord",    services: t("Landing.members_boosts_emoji", lang),           color: "#5865F2", gradient: "linear-gradient(135deg,#5865F2,#8891F2)", available: false },
    { Icon: SnapchatSVG,  name: "Snapchat",   platformKey: "snapchat",   services: t("Landing.followers_views", lang),                                color: "#FFFC00", gradient: "linear-gradient(135deg,#FFFC00,#f0c000)", available: false },
    { Icon: SpotifySVG,   name: "Spotify",    platformKey: "spotify",    services: t("Landing.followers_streams_plays", lang),        color: "#1DB954", gradient: "linear-gradient(135deg,#1DB954,#158a3e)", available: false },
  ];

  const features = [
    { icon: Zap, title: t("features.fast", lang), desc: t("features.fast_desc", lang), color: "#FF6B35", gradient: "linear-gradient(135deg,#FF6B35,#FF4500)" },
    { icon: Shield, title: t("features.safe", lang), desc: t("features.safe_desc", lang), color: "#8B5CF6", gradient: "linear-gradient(135deg,#8B5CF6,#4F46E5)" },
    { icon: Star, title: t("features.quality", lang), desc: t("features.quality_desc", lang), color: "#FBBF24", gradient: "linear-gradient(135deg,#FBBF24,#F59E0B)" },
    { icon: Headphones, title: t("features.support", lang), desc: t("features.support_desc", lang), color: "#0EA5E9", gradient: "linear-gradient(135deg,#0EA5E9,#0284C7)" },
  ];

  const isRTL = lang === "he";
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <div className="min-h-screen" data-testid="landing-page" dir={isRTL ? "rtl" : "ltr"}>
      {/* ══════════════════════════════════════════════════════════════════════
          HERO SECTION
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="flex flex-col items-center justify-center px-6 pb-24 pt-36">
        <div className="max-w-4xl w-full mx-auto text-center">

          {/* Main headline */}
          <h1 className="font-black font-outfit mb-6 animate-fade-in-up"
            style={{ fontSize: "clamp(3.5rem, 11vw, 7.5rem)", lineHeight: 1.0, letterSpacing: "-0.03em", animationDelay: "0ms" }}>
            <span style={{ color: "var(--text-primary)" }}>
              {t("Landing.your_growth", lang)}
            </span>
            <br />
            <span style={{ color: "#FF6B35" }}>
              {t("Landing.starts_here", lang)}
            </span>
          </h1>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fade-in-up" style={{ animationDelay: "100ms" }}>
            <button
              onClick={() => user ? navigate("/shop") : login()}
              className="landing-btn-primary rounded-full px-10 py-4 text-white font-black text-lg"
              style={{ background: "#8B5CF6" }}
              data-testid="hero-cta-btn">
              <Rocket className="inline-block w-5 h-5 me-2" />
              {t("Landing.start_growing_now", lang)}
            </button>
            <button
              onClick={() => user ? navigate("/dashboard") : login()}
              className="landing-btn-ghost rounded-full px-8 py-4 font-bold text-base"
              style={{ background: "var(--surface-1)", border: "1px solid var(--surface-2)", color: "var(--text-primary)" }}
              data-testid="hero-free-gifts-btn">
              <Play className="inline-block w-5 h-5 me-2" style={{ color: "#E1306C" }} />
              {t("Landing.try_our_system", lang)}
            </button>
          </div>

          {/* Platform icons */}
          <div className="flex items-center justify-center gap-2.5 mb-14 flex-wrap">
            {[
              { Icon: InstagramSVG, gradient: "linear-gradient(135deg,#833AB4,#E1306C)" },
              { Icon: TikTokSVG,   gradient: "linear-gradient(135deg,#69C9D0,#EE1D52)" },
              { Icon: YouTubeSVG,  gradient: "linear-gradient(135deg,#FF0000,#CC0000)" },
              { Icon: TwitchSVG,   gradient: "linear-gradient(135deg,#6441A4,#9146FF)" },
              { Icon: TelegramSVG, gradient: "linear-gradient(135deg,#1E90FF,#2AABEE)" },
              { Icon: TwitterSVG,  gradient: "linear-gradient(135deg,#333,#1A8CD8)" },
              { Icon: FacebookSVG, gradient: "linear-gradient(135deg,#0866FF,#1877F2)" },
              { Icon: DiscordSVG,  gradient: "linear-gradient(135deg,#5865F2,#8891F2)" },
            ].map(({ Icon, gradient }, i) => (
              <div key={i} className="w-10 h-10 rounded-xl flex items-center justify-center animate-fade-in-up"
                style={{ background: gradient, color: "#fff", animationDelay: `${200 + i * 50}ms` }}>
                <Icon size={18} />
              </div>
            ))}
          </div>

          {/* Stats — 2×2 grid on mobile, 4-col row on sm+ */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xs sm:max-w-lg mx-auto">
            {[
              { to: 1200, suffix: "+", label: t("Landing.happy_clients", lang), color: "#8B5CF6" },
              { to: 3800, suffix: "+", label: t("Landing.orders_fulfilled", lang), color: "#E1306C" },
              { fixed: PLATFORMS.filter(p => p.available).length + "+", label: t("Landing.platforms", lang), color: "#F77737" },
              { fixed: "4.9★", label: t("Landing.avg_rating", lang), color: "#FBBF24" },
            ].map((s, i) => (
              <div key={i}
                className="flex flex-col items-center justify-center py-4 px-3 rounded-2xl animate-fade-in-up"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid var(--surface-2)",
                  animationDelay: `${600 + i * 60}ms`,
                }}>
                <div className="text-2xl font-black font-outfit" style={{ color: s.color, fontVariantNumeric: "tabular-nums" }} dir="ltr">
                  {s.to ? <AnimatedCounter to={s.to} suffix={s.suffix} /> : s.fixed}
                </div>
                <div className="text-xs mt-1 text-center leading-tight" style={{ color: "var(--text-secondary)" }}>{s.label}</div>
              </div>
            ))}
          </div>

        </div>
      </section>

      <div className="section-divider" />

      {/* ══════════════════════════════════════════════════════════════════════
          PLATFORMS GRID
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="px-6 md:px-12 lg:px-24 py-24" data-testid="platforms-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14 scroll-reveal">
            <h2 className="text-3xl md:text-5xl font-black font-outfit" style={{ color: "var(--text-primary)", letterSpacing: "-0.02em" }}>
              {t("Landing.one_platform", lang)}{" "}
              <span style={{ color: "#8B5CF6" }}>
                {t("Landing.all_networks", lang)}
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {PLATFORMS.map(({ Icon, name, services, color, gradient, available, platformKey }, i) => {
              const inner = (
                <>
                  {!available && (
                    <div className="absolute top-2.5 end-2.5 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
                      {t("Shop.soon", lang)}
                    </div>
                  )}
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                    style={{ background: gradient, color: "#fff", opacity: available ? 1 : 0.5 }}>
                    <Icon size={20} />
                  </div>
                  <div className="font-black text-sm font-outfit mb-1"
                    style={{ color: available ? "var(--text-primary)" : "var(--text-secondary)" }}>
                    {name}
                  </div>
                  <div className="text-[10px] leading-4" style={{ color: "var(--text-secondary)" }}>
                    {services}
                  </div>
                  {available && (
                    <div className="mt-3 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#22c55e" }} />
                      <span className="text-[10px] font-bold" style={{ color: "#22c55e" }}>{t("Landing.live", lang)}</span>
                    </div>
                  )}
                </>
              );
              const cardStyle = {
                transitionDelay: `${i * 0.06}s`,
                background: "var(--surface-1)",
                border: "1px solid var(--surface-2)",
              };
              return available ? (
                <Link key={name} to={`/shop?platform=${platformKey}`}
                  className="relative platform-card-hover rounded-2xl p-5 scroll-reveal block"
                  style={cardStyle}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = `${color}40`;
                    e.currentTarget.style.boxShadow = `0 4px 24px ${color}15`;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = "";
                    e.currentTarget.style.boxShadow = "";
                  }}>
                  {inner}
                </Link>
              ) : (
                <div key={name}
                  className="relative rounded-2xl p-5 scroll-reveal"
                  style={{ ...cardStyle, opacity: 0.5 }}>
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ══════════════════════════════════════════════════════════════════════
          COMPARISON SECTION
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-6 md:px-12 lg:px-24" data-testid="comparison-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 scroll-reveal">
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-bold mb-5"
              style={{ background: "rgba(225,48,108,0.08)", border: "1px solid rgba(225,48,108,0.25)", color: "#E1306C" }}>
              💡 {t("Landing.the_social_proof_effect", lang)}
            </div>
            <h2 className="text-3xl md:text-4xl font-black font-outfit mb-3" style={{ color: "var(--text-primary)", letterSpacing: "-0.02em" }}>
              {t("Landing.which_profile_would_you", lang)}{" "}
              <span style={{ color: "#E1306C" }}>
                {t("Landing.follow_or_buy_from", lang)}
              </span>
            </h2>
            <p className="text-sm max-w-md mx-auto" style={{ color: "var(--text-secondary)" }}>
              {t("Landing.people_make_decisions_based_on_social_signals_wh", lang)}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 md:gap-6 max-w-2xl mx-auto scroll-reveal">
            {/* Profile A — low numbers */}
            <div className="rounded-2xl overflow-hidden relative"
              style={{ border: "1px solid var(--surface-2)", background: "var(--surface-1)", opacity: 0.7 }}>
              <div className="absolute top-2.5 start-2.5 z-10">
                <span className="text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider"
                  style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.25)" }}>
                  ✗ {t("Landing.without_boost", lang)}
                </span>
              </div>
              {/* Avatar */}
              <div className="pt-8 px-4 pb-4 text-center">
                <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center font-black text-xl"
                  style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>A</div>
                <div className="text-xs font-bold mb-0.5" style={{ color: "var(--text-primary)" }}>@mybrand</div>
                <div className="text-[10px]" style={{ color: "var(--text-secondary)" }}>Fashion · Tel Aviv</div>
              </div>
              {/* Stats */}
              <div className="grid grid-cols-3 border-t" style={{ borderColor: "var(--surface-2)" }}>
                {[
                  { n: "214", l: t("Landing.posts", lang) },
                  { n: "183", l: t("Landing.followers", lang) },
                  { n: "7", l: t("Landing.following", lang) },
                ].map((s, i) => (
                  <div key={i} className="py-3 text-center border-e last:border-e-0" style={{ borderColor: "var(--surface-2)" }}>
                    <div className="text-sm font-black font-outfit" style={{ color: "var(--text-primary)" }}>{s.n}</div>
                    <div className="text-[9px]" style={{ color: "var(--text-secondary)" }}>{s.l}</div>
                  </div>
                ))}
              </div>
              {/* Last post likes */}
              <div className="px-4 py-3 border-t flex items-center gap-2" style={{ borderColor: "var(--surface-2)" }}>
                <Heart className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--text-secondary)" }} />
                <span className="text-xs" style={{ color: "var(--text-secondary)" }}>4 {t("Landing.likes_on_last_post", lang)}</span>
              </div>
            </div>

            {/* Profile B — boosted */}
            <div className="rounded-2xl overflow-hidden relative"
              style={{ border: "1px solid rgba(34,197,94,0.45)", background: "var(--surface-1)", boxShadow: "0 0 28px rgba(34,197,94,0.1)" }}>
              <div className="absolute top-2.5 start-2.5 z-10">
                <span className="text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider"
                  style={{ background: "rgba(34,197,94,0.15)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.3)" }}>
                  ✓ {t("Landing.with_ifollowers", lang)}
                </span>
              </div>
              {/* Avatar */}
              <div className="pt-8 px-4 pb-4 text-center">
                <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center font-black text-xl text-white"
                  style={{ background: "linear-gradient(135deg,#833AB4,#E1306C)" }}>A</div>
                <div className="text-xs font-bold mb-0.5 flex items-center justify-center gap-1" style={{ color: "var(--text-primary)" }}>
                  @mybrand <BadgeCheck className="w-3.5 h-3.5" style={{ color: "#1A8CD8" }} />
                </div>
                <div className="text-[10px]" style={{ color: "var(--text-secondary)" }}>Fashion · Tel Aviv</div>
              </div>
              {/* Stats */}
              <div className="grid grid-cols-3 border-t" style={{ borderColor: "rgba(34,197,94,0.2)" }}>
                {[
                  { n: "214", l: t("Landing.posts", lang) },
                  { n: "8,400", l: t("Landing.followers", lang), highlight: true },
                  { n: "7", l: t("Landing.following", lang) },
                ].map((s, i) => (
                  <div key={i} className="py-3 text-center border-e last:border-e-0" style={{ borderColor: "rgba(34,197,94,0.15)" }}>
                    <div className={`text-sm font-black font-outfit ${s.highlight ? "" : ""}`}
                      style={{ color: s.highlight ? "#22c55e" : "var(--text-primary)" }}>{s.n}</div>
                    <div className="text-[9px]" style={{ color: "var(--text-secondary)" }}>{s.l}</div>
                  </div>
                ))}
              </div>
              {/* Last post likes */}
              <div className="px-4 py-3 border-t flex items-center gap-2" style={{ borderColor: "rgba(34,197,94,0.2)" }}>
                <Heart className="w-3.5 h-3.5 flex-shrink-0 fill-current" style={{ color: "#E1306C" }} />
                <span className="text-xs font-semibold" style={{ color: "#22c55e" }}>1,247 {t("Landing.likes_on_last_post", lang)} 🔥</span>
              </div>
            </div>
          </div>

          {/* Arrow label */}
          <div className="flex items-center justify-center mt-8 scroll-reveal">
            <div className="rounded-2xl px-6 py-4 text-center max-w-sm"
              style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.2)" }}>
              <div className="text-sm font-bold mb-1" style={{ color: "var(--text-primary)" }}>
                {t("Landing.the_right_profile_wins_every_time", lang)}
              </div>
              <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                {t("Landing.visitors_trust_follow_and_buy_from_profiles_that", lang)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ══════════════════════════════════════════════════════════════════════
          HOW IT WORKS
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="how-it-works"
        style={{ background: "var(--surface-1)" }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16 scroll-reveal">
            <h2 className="text-3xl md:text-5xl font-black font-outfit mb-3" style={{ color: "var(--text-primary)", letterSpacing: "-0.02em" }}>
              {t("Landing.3_steps", lang)}{" "}
              <span style={{ color: "#FF6B35" }}>
                {t("Landing.that_s_it", lang)}
              </span>
            </h2>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              {t("Landing.from_zero_to_growing_in_under_5_minutes", lang)}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                n: "01", icon: Globe2, color: "#E1306C",
                title: t("Landing.choose_your_service", lang),
                desc: t("Landing.pick_followers_likes_or_views_set_the_quantity", lang),
              },
              {
                n: "02", icon: Lock, color: "#8B5CF6",
                title: t("Landing.enter_link_pay", lang),
                desc: t("Landing.paste_your_public_link_no_password_needed", lang),
              },
              {
                n: "03", icon: TrendingUp, color: "#22c55e",
                title: t("Landing.watch_results_come_in", lang),
                desc: t("Landing.order_starts_within_hours_track_live_in_dashboar", lang),
              },
            ].map((step, i) => (
              <div key={i} className="scroll-reveal step-card-hover relative overflow-hidden rounded-2xl p-6"
                style={{ transitionDelay: `${i * 0.12}s`, background: "var(--bg-primary)", border: "1px solid var(--surface-2)" }}>
                {/* Big number background */}
                <div className="absolute top-2 end-4 text-7xl font-black font-outfit select-none pointer-events-none"
                  style={{ color: `${step.color}08`, lineHeight: 1 }}>{step.n}</div>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 relative z-10"
                  style={{ background: `${step.color}18`, color: step.color }}>
                  <step.icon className="w-5 h-5" />
                </div>
                <div className="text-[10px] font-black tracking-widest uppercase mb-2 relative z-10" style={{ color: step.color }}>{step.n}</div>
                <h3 className="font-black font-outfit text-base mb-2 relative z-10" style={{ color: "var(--text-primary)" }}>{step.title}</h3>
                <p className="text-sm leading-6 relative z-10" style={{ color: "var(--text-secondary)" }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ══════════════════════════════════════════════════════════════════════
          WHY IT WORKS
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="px-6 md:px-12 lg:px-24 py-24" data-testid="trust-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14 scroll-reveal">
            <h2 className="text-3xl md:text-5xl font-black font-outfit mb-4" style={{ color: "var(--text-primary)", letterSpacing: "-0.02em" }}>
              {t("Landing.strong_social_proof", lang)}{" "}
              <span style={{ color: "#FF6B35" }}>
                {t("Landing.earns_trust_faster", lang)}
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                icon: Lock, color: "#22c55e",
                stat: t("Landing.0_passwords", lang),
                title: t("Landing.no_password_needed", lang),
                desc: t("Landing.only_a_public_link_your_account_stays_100_secure", lang),
                badge: t("features.safe", lang),
              },
              {
                icon: Zap, color: "#FBBF24",
                stat: t("Landing.2h_start", lang),
                title: t("Landing.lightning_fast_start", lang),
                desc: t("Landing.order_kicks_off_within_hours_of_payment_confirma", lang),
                badge: t("Landing.instant_queue", lang),
              },
              {
                icon: TrendingUp, color: "#E1306C",
                stat: t("Landing.3x_trust", lang),
                title: t("Landing.first_impression_wins", lang),
                desc: t("Landing.active_profiles_get_3_more_inquiries_follows_and", lang),
                badge: t("Landing.proven", lang),
              },
            ].map((item, i) => (
              <div key={i} className="feature-card-hover scroll-reveal rounded-2xl p-5 flex gap-5 relative overflow-hidden"
                style={{ transitionDelay: `${i * 0.1}s`, background: "var(--surface-1)", border: `1px solid ${item.color}20` }}>
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: `${item.color}15`, color: item.color }}>
                    <item.icon className="w-6 h-6" />
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-2xl font-black font-outfit leading-none mb-1.5" style={{ color: item.color }} dir="ltr">{item.stat}</div>
                  <div className="inline-block rounded-full px-2 py-0.5 text-[10px] font-black mb-2"
                    style={{ background: `${item.color}15`, color: item.color, border: `1px solid ${item.color}30` }}>
                    {item.badge}
                  </div>
                  <h3 className="font-black text-sm mb-1" style={{ color: "var(--text-primary)" }}>{item.title}</h3>
                  <p className="text-xs leading-5" style={{ color: "var(--text-secondary)" }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ══════════════════════════════════════════════════════════════════════
          FEATURES
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="features-section"
        style={{ background: "var(--surface-1)" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14 scroll-reveal">
            <h2 className="text-3xl md:text-5xl font-black font-outfit" style={{ color: "var(--text-primary)", letterSpacing: "-0.02em" }}>
              {t("features.title", lang)}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((f, i) => (
              <div key={i} className="feature-card-hover scroll-reveal rounded-2xl p-5 relative overflow-hidden group"
                style={{ transitionDelay: `${i * 0.1}s`, background: "var(--bg-primary)", border: "1px solid var(--surface-2)" }}
                data-testid={`feature-card-${i}`}>
                {/* Hover glow */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none rounded-2xl"
                  style={{ background: `radial-gradient(ellipse at top left, ${f.color}08, transparent 70%)`, transition: "opacity 200ms cubic-bezier(0.23, 1, 0.32, 1)" }} />
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 relative z-10"
                  style={{ background: f.gradient, color: "#fff" }}>
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="font-black text-sm mb-1.5 relative z-10" style={{ color: "var(--text-primary)" }}>{f.title}</h3>
                <p className="text-xs leading-5 relative z-10" style={{ color: "var(--text-secondary)" }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ══════════════════════════════════════════════════════════════════════
          TESTIMONIALS
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="testimonials">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14 scroll-reveal">
            <h2 className="text-3xl md:text-5xl font-black font-outfit" style={{ color: "var(--text-primary)", letterSpacing: "-0.02em" }}>
              {t("Landing.real_results", lang)}{" "}
              <span style={{ color: "#F59E0B" }}>
                {t("Landing.real_people", lang)}
              </span>
            </h2>
          </div>

          <ReviewCarousel reviews={shuffledReviews} lang={lang} />
        </div>
      </section>

      <div className="section-divider" />

      {/* ══════════════════════════════════════════════════════════════════════
          MID-PAGE CTA
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-6 md:px-12 lg:px-24 scroll-reveal">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-bold mb-6"
            style={{ background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.25)", color: "#8B5CF6" }}>
            <Sparkles className="w-3.5 h-3.5" />
            {lang === "he" ? "מוכן לצמוח?" : lang === "ru" ? "Готов расти?" : "Ready to grow?"}
          </div>
          <h2 className="text-3xl md:text-5xl font-black font-outfit mb-4" style={{ color: "var(--text-primary)", letterSpacing: "-0.02em" }}>
            {lang === "he" ? "הצטרף ל-1,200+ יוצרים" : lang === "ru" ? "Присоединяйтесь к 1,200+" : "Join 1,200+ creators"}
          </h2>
          <p className="text-base mb-8" style={{ color: "var(--text-secondary)" }}>
            {lang === "he"
              ? "הזמנות מ-₪19 · מתחיל תוך שעות · ללא סיסמה"
              : lang === "ru"
              ? "Заказы от ₪19 · Старт в течение часов · Без пароля"
              : "Orders from ₪19 · Starts within hours · No password needed"}
          </p>
          <button
            onClick={() => user ? navigate("/shop") : login()}
            className="landing-btn-primary rounded-full px-10 py-4 text-white font-black text-lg inline-flex items-center gap-2"
            style={{ background: "#8B5CF6" }}>
            <Rocket className="w-5 h-5" />
            {t("Landing.start_growing_now", lang)}
          </button>
        </div>
      </section>

      <div className="section-divider" />

      {/* ══════════════════════════════════════════════════════════════════════
          FAQ
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="faq-section"
        style={{ background: "var(--surface-1)" }}>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10 scroll-reveal">
            <h2 className="text-3xl md:text-4xl font-black font-outfit mb-2" style={{ color: "var(--text-primary)", letterSpacing: "-0.02em" }}>
              {t("Landing.common", lang)}{" "}
              <span style={{ color: "#0EA5E9" }}>
                {t("Landing.questions", lang)}
              </span>
            </h2>
          </div>

          <div className="space-y-2">
            {[
              {
                q: t("Landing.when_will_the_followers_start_arriving", lang),
                a: t("Landing.in_many_cases_the_start_is_relatively_quick_but_", lang),
                icon: Clock3,
              },
              {
                q: t("Landing.how_do_we_do_this", lang),
                a: t("Landing.using_advanced_technologies_api_integrations_and", lang),
                icon: Zap,
              },
              {
                q: t("Landing.is_this_bots", lang),
                a: t("Landing.part_of_the_service_is_based_on_smart_automation", lang),
                icon: Shield,
              },
              {
                q: t("Landing.do_you_have_a_warranty", lang),
                a: null,
                icon: BadgeCheck,
              },
            ].map((faq, i) => (
              <div key={i} className="scroll-reveal rounded-2xl overflow-hidden"
                style={{
                  transitionDelay: `${i * 0.08}s`,
                  border: `1px solid ${faqOpen === i ? "rgba(225,48,108,0.3)" : "var(--surface-2)"}`,
                  background: faqOpen === i ? "rgba(225,48,108,0.04)" : "var(--bg-primary)",
                  transition: "border-color 0.2s, background 0.2s",
                }}>
                <button
                  className="w-full text-start px-6 py-5 flex items-center gap-4"
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  aria-expanded={faqOpen === i}
                  style={{ color: "var(--text-primary)" }}>
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
                    <faq.icon className="w-4 h-4" />
                  </div>
                  <span className="flex-1 text-sm font-semibold">{faq.q}</span>
                  <ChevronDown className="w-4 h-4 flex-shrink-0"
                    style={{ color: "var(--text-secondary)", transform: faqOpen === i ? "rotate(180deg)" : "rotate(0deg)", transition: prefersReducedMotion ? "none" : "transform 160ms cubic-bezier(0.23, 1, 0.32, 1)" }} />
                </button>
                {faqOpen === i && (
                  <div className="px-6 pb-5 text-sm leading-7 whitespace-pre-line"
                    style={{ color: "var(--text-secondary)", borderTop: "1px solid var(--surface-2)", paddingTop: 12 }}>
                    {i === 3 ? (
                      <>
                        {t("Landing.yes_the_warranty_depends_on_compliance_with_the_", lang)}
                        <Link to="/warranty"
                          className="inline-flex items-center gap-1 font-semibold underline underline-offset-2 transition-opacity hover:opacity-80"
                          style={{ color: "#E1306C" }}>
                          {t("Landing.official_warranty_policy", lang)}
                        </Link>
                      </>
                    ) : faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Link to full FAQ */}
          <div className="text-center mt-8 scroll-reveal">
            <Link to="/faq"
              className="inline-flex items-center gap-2 text-sm font-semibold transition-opacity hover:opacity-80"
              style={{ color: "#0EA5E9" }}>
              {t("Landing.see_all_frequently_asked_questions", lang)}
              <ArrowRight className="w-4 h-4" style={{ transform: lang === "he" ? "scaleX(-1)" : "none" }} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Scroll to top button ── */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Scroll to top"
        style={{
          position: "fixed",
          right: "1.25rem",
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.25rem)",
          zIndex: 108,
          width: 44,
          height: 44,
          borderRadius: "50%",
          background: "#8B5CF6",
          color: "#fff",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 16px rgba(139,92,246,0.45)",
          touchAction: "manipulation",
          opacity: showScrollTop && !supportOpen ? 1 : 0,
          transform: showScrollTop && !supportOpen ? "translateY(0) scale(1)" : "translateY(16px) scale(0.85)",
          pointerEvents: showScrollTop && !supportOpen ? "auto" : "none",
          transition: prefersReducedMotion ? "none" : "opacity 0.25s ease, transform 0.25s ease",
        }}>
        <ChevronUp className="w-5 h-5" />
      </button>
    </div>
  );
}
